import React from 'react';
import I18n from 'i18n-js';
import { times } from 'lodash';
import classNames from 'classnames';
import withTrendingSearches from '../../with_trending_searches';
import { reverb_personalization_TrendingSearch } from '@reverbdotcom/commons/src/gql/graphql';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { SearchIcon } from '@reverbdotcom/cadence/icons/react';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import categories from '../category_data';
import { OverflowingRow } from '@reverbdotcom/commons';
import { RCGrid, RCTextWithIcon } from '@reverbdotcom/cadence/components';

interface IProps {
  trendingSearches?: reverb_personalization_TrendingSearch[];
  loading?: boolean;
}

const COMPONENT_NAME = 'HomepageTrendingSearches';
const TRENDING_SEARCHES_COUNT = 3;
const IMAGES_COUNT = 4;
const LIVE = 'live';

function trendingSearchesWithResults(trendingSearches): reverb_personalization_TrendingSearch[] | [] {
  if (!trendingSearches) { return []; }
  return trendingSearches.filter((ts) => ts.listings.some(l => l.state === LIVE));
}

function organizeImages(listings, categoryUuid) {
  const images = [];
  if (!listings) { return images; }
  times(IMAGES_COUNT, (idx) => {
    listings.forEach(listing => {
      if (images.length === IMAGES_COUNT) { return images; }
      if (listing?.images?.length > idx) {
        images.push(listing.images[idx]);
      }
    });
  });

  if (images.length < IMAGES_COUNT) {
    let backupImages = [];
    const categoryData = categories.find(cat => cat.uuid === categoryUuid);
    if (categoryData) {
      backupImages = [
        { source: categoryData.image_v2 },
        { source: categoryData.image },
        { source: categoryData.icon },
      ];
    }
    images.push(...backupImages);
  }
  return images;
}

function getBackgroundImageStyle(images, idx) {
  const imageUrl = images[idx]?.source;
  return { backgroundImage: `url(${imageUrl})` };
}

function renderPlaceholder() {
  const classes = classNames(
    'marketing-callout',
    'marketing-callout--placeholder',
  );
  return (
    <div className={classes}>
      <div className="marketing-callout__image-area">
        <div className="marketing-callout__image" />
      </div>
      <div className="marketing-callout__text-area">
        <div className="marketing-callout__text">
          <div className="marketing-callout__title" />
        </div>
      </div>
    </div>
  );
}

function buildClickEvent(trendingSearch, position) {
  return {
    eventName: MParticleEventName.ClickedTrendingSearchesTile,
    componentName: COMPONENT_NAME,
    query: trendingSearch.query,
    position,
  };
}

function TrendingSearch({ trendingSearch, idx }) {
  const productCategory = I18n.t(`discovery.taxonomy.${trendingSearch.categoryUuid}`);
  const classes = classNames(
    'marketing-callout',
    'homepage-trending-searches',
  );
  const images = organizeImages(trendingSearch?.listings, trendingSearch?.categoryUuid);

  return (
    <CoreLink
      className={classes}
      key={`homepage-trending-search-${idx}`}
      clickEvent={buildClickEvent(trendingSearch, idx)}
      to={Paths.marketplace.expand({
        query: trendingSearch.query,
        product_type: trendingSearch.categorySlug,
        referer: 'trending_search' })}
    >
      <div className="marketing-callout__inner" key={idx}>
        <div className="marketing-callout__image-area">
          {times(IMAGES_COUNT, (listingIdx) => {
            return (
              <div
                className="marketing-callout__image-container"
                key={`marketing-callout-listing-photo-${listingIdx}`} >
                <div
                  className="marketing-callout__image"
                  style={getBackgroundImageStyle(images, listingIdx)}
                />
              </div>
            );
          })}
        </div>
        <div className="marketing-callout__text-area">
          <div className="marketing-callout__text">
            <div className="marketing-callout__text__query">
              <RCTextWithIcon svgComponent={SearchIcon} placement="right">
                {trendingSearch.query}
              </RCTextWithIcon>
            </div>
            <div className="marketing-callout__text__category">
              {I18n.t('discovery.homepage.trendingSearches.trendingIn', { productCategory: productCategory })}
            </div>
          </div>
        </div>
      </div>
    </CoreLink>
  );
}

export function HomepageTrendingSearches(props: IProps) {
  const { loading } = props;
  const user = useUser();
  const trendingSearches = trendingSearchesWithResults(props.trendingSearches);
  const hasTrendingSearches = !loading && !!trendingSearches?.length;
  if (!hasTrendingSearches) { return null; }

  const searchCards = !!trendingSearches.length && times(TRENDING_SEARCHES_COUNT, (idx) => {
    return (
      <TrendingSearch
        trendingSearch={trendingSearches[idx]}
        idx={idx}
        key={idx}
      />
    );
  });

  function renderGridPlaceholder() {
    return (
      times(TRENDING_SEARCHES_COUNT, index => (
        <RCGrid.Item
          colSpan={{ desktop: 4, mobile: 12 }}
          key={index}
        >
          {renderPlaceholder()}
        </RCGrid.Item>
      ))
    );
  }

  return (
    <OverflowingRow
      title={I18n.t(`discovery.homepage.trendingSearches.${user.loggedOut ? 'loggedOut' : 'loggedIn' }.title`)}
    >
      <RCGrid disableAndPulse={loading} singleRowOn="all">
        {loading ? (
          renderGridPlaceholder()
        ) : (
          searchCards.map((card, index) => (
            <RCGrid.Item
              colSpan={{ desktop: 4, tablet: 5, mobile: 8 }}
              key={trendingSearches[index]?.query}
            >
              {card}
            </RCGrid.Item>
          ))
        )}
      </RCGrid>
    </OverflowingRow>
  );
}

export default withTrendingSearches<IProps>(HomepageTrendingSearches);
